import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles, categories, siteConfig } from '@/mocks/data';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);
  const category = article ? categories.find((c) => c.slug === article.category) : null;
  const relatedArticles = article
    ? articles.filter((a) => a.category === article.category && a.slug !== article.slug).slice(0, 3)
    : [];

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - ${siteConfig.name}`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', article.excerpt);
    } else {
      document.title = `文章未找到 - ${siteConfig.name}`;
    }
  }, [article]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-16 px-4 md:px-6">
          <div className="max-w-7xl mx-auto text-center py-20">
            <h1 className="text-2xl font-heading font-semibold text-foreground-900 mb-4">文章未找到</h1>
            <p className="text-foreground-600 mb-6">你访问的文章页面不存在</p>
            <Link
              to="/"
              className="inline-flex px-5 py-2.5 bg-primary-500 text-white rounded-md font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.publishDate,
            publisher: {
              '@type': 'Organization',
              name: siteConfig.name,
            },
            image: article.imageUrl,
            articleSection: category?.name,
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: article.title,
            description: article.excerpt,
            url: `${siteConfig.url}/article/${article.slug}`,
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <article className="pt-20 pb-16 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="mb-4">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: category?.name || '分类', to: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>

            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="text-xs px-3 py-1 bg-accent-100 text-accent-700 rounded-full font-medium hover:bg-accent-200 transition-colors duration-200 whitespace-nowrap"
                  >
                    {category.name}
                  </Link>
                )}
                <span className="text-xs text-foreground-500">{article.readTime} 阅读</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-3 text-sm text-foreground-500">
                <span>{article.author}</span>
                <span>·</span>
                <span>{article.publishDate}</span>
              </div>
            </header>

            <figure className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover"
              />
            </figure>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed
                prose-h2:text-foreground-900 prose-h2:font-heading prose-h2:text-xl md:prose-h2:text-2xl prose-h2:font-semibold prose-h2:mt-8 prose-h2:mb-4
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                prose-strong:text-foreground-900 prose-strong:font-semibold
                prose-li:text-foreground-700"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 pt-6 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <section className="mt-12 pt-8 border-t border-background-200/70">
                <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-6">相关文章</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-100 rounded-lg overflow-hidden"
                    >
                      <div className="overflow-hidden aspect-[16/10]">
                        <img
                          src={ra.imageUrl}
                          alt={ra.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-3">
                        <h3 className="text-sm font-heading font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                          {ra.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-1">{ra.publishDate}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
}